import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Modal from "../common_modal/Modal";
import dict from "../../Data/dict.json";
import { editGeometry, editGeometryV2 } from "../../App/actions/layerActions";

export default function C_E_LATLNG({
  config,
  inPopup,
  onClose,
  language = "id",
}) {
  const properties_state = useSelector((state) => state.properties);

  const { row, field } = config;
  const dispatch = useDispatch();
  const layer_state = useSelector((state) => state.layer);

  const [lat, setLat] = useState("");
  const [lon, setLon] = useState("");
  const [showPopupModal, setShowPopupModal] = useState(false);

  // Check if geometry is Point (or undefined/null for brand new features)
  const geomType = row?.geometry?.type;
  const isPoint = !geomType || geomType === "Point";
  const coordinates = row?.geometry?.coordinates;

  // Extract from GeoJSON geometry on mount and when row updates
  useEffect(() => {
    if (isPoint) {
      // GeoJSON Point format: [longitude, latitude]
      if (Array.isArray(coordinates) && coordinates.length >= 2) {
        setLon(coordinates[0]); // [0] is Longitude (X)
        setLat(coordinates[1]); // [1] is Latitude (Y)
      }
    }
  }, [row, isPoint, coordinates]);

  const handleCloseModal = () => {
    if (inPopup) {
      setShowPopupModal(false);
    }

    // Close it from the parent table if applicable
    if (onClose) {
      onClose();
    }

    // Revert local state to original if closed without saving
    if (isPoint && Array.isArray(coordinates) && coordinates.length >= 2) {
      setLon(coordinates[0]);
      setLat(coordinates[1]);
    }
  };

  const handleSaveClick = () => {
    // We format it back into a GeoJSON coordinate array [lon, lat]
    const newCoordinates = [parseFloat(lon), parseFloat(lat)];

    // Pull layer context from Redux
    const layer_id = layer_state?.layer_id;
    const geo_layer = layer_state?.geo_layer_list?.find(
      (item) => item.geo_layer._id === layer_id,
    )?.geo_layer;
    const current_architecture = geo_layer?.architecture;

    const feature_key = row?.key || row?.id;

    // Construct the updated geometry object, preserving the type (defaulting to Point)
    const geometry = {
      ...(row?.geometry || { type: "Point" }),
      coordinates: newCoordinates,
    };

    // Trigger your API actions
    if (feature_key && geometry && layer_id) {
      const body = {
        feature_key: feature_key,
        geo_layer_id: layer_id,
        geometry: geometry,
      };

      if (current_architecture === "v2") {
        dispatch(editGeometryV2(body));
      } else {
        dispatch(editGeometry(body));
      }
    }

    // Close the modal after dispatching
    if (inPopup) {
      setShowPopupModal(false);
    }
    if (onClose) {
      onClose();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && isPoint) {
      e.preventDefault();
      handleSaveClick();
    }
  };

  // Format display value for the preview based on field type
  let displayValue = "-";
  if (isPoint && Array.isArray(coordinates) && coordinates.length >= 2) {
    if (field?.type === "latitude") {
      displayValue = String(coordinates[1]); // Only Latitude
    } else if (field?.type === "longitude") {
      displayValue = String(coordinates[0]); // Only Longitude
    } else {
      displayValue = `${coordinates[1]}, ${coordinates[0]}`; // Fallback Lat, Lon
    }
  } else if (!isPoint) {
    displayValue = `Unsupported (${geomType})`;
  }

  // The actual modal window
  const modalContent = (
    <Modal
      modalSize="small"
      id={`modal-latlng-${row?.id}`}
      isOpen={true}
      onClose={handleCloseModal}
      title={dict?.["Edit Coordinate"]?.[language] || "Edit Coordinate"}
    >
      <div
        className="box-body"
        style={{
          display: "flex",
          flexDirection: "column",
          padding: "20px",
          gap: "16px",
        }}
      >
        {isPoint ? (
          <>
            {/* Latitude Input (Y) */}
            <div
              style={{ display: "flex", flexDirection: "column", gap: "8px" }}
            >
              <label
                style={{
                  fontSize: "14px",
                  color: "#334155",
                  fontWeight: "500",
                }}
              >
                {dict?.["Latitude"]?.[language] || "Latitude"} (Y)
              </label>
              <input
                type="number"
                step="any"
                value={lat}
                onChange={(e) => setLat(e.target.value)}
                onKeyDown={handleKeyDown}
                style={{
                  border: "1px solid #cbd1d8",
                  borderRadius: "4px",
                  padding: "8px 12px",
                  fontSize: "14px",
                  width: "100%",
                  boxSizing: "border-box",
                  outline: "none",
                }}
                placeholder="e.g. -6.2088"
                autoFocus
              />
            </div>

            {/* Longitude Input (X) */}
            <div
              style={{ display: "flex", flexDirection: "column", gap: "8px" }}
            >
              <label
                style={{
                  fontSize: "14px",
                  color: "#334155",
                  fontWeight: "500",
                }}
              >
                {dict?.["Longitude"]?.[language] || "Longitude"} (X)
              </label>
              <input
                type="number"
                step="any"
                value={lon}
                onChange={(e) => setLon(e.target.value)}
                onKeyDown={handleKeyDown}
                style={{
                  border: "1px solid #cbd1d8",
                  borderRadius: "4px",
                  padding: "8px 12px",
                  fontSize: "14px",
                  width: "100%",
                  boxSizing: "border-box",
                  outline: "none",
                }}
                placeholder="e.g. 106.8456"
              />
            </div>

            {/* Actions */}
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "10px",
                marginTop: "10px",
              }}
            >
              <button
                onClick={handleCloseModal}
                className="button outline_grey"
                style={{
                  backgroundColor: "#f1f5f9",
                }}
              >
                {dict?.["Cancel"]?.[language] || "Cancel"}
              </button>
              <button
                onClick={handleSaveClick}
                className="button background_black"
              >
                {dict?.["Save"]?.[language] || "Save"}
              </button>
            </div>
          </>
        ) : (
          /* Warning State for Non-Point Geometries */
          <div style={{ textAlign: "center", padding: "10px 0" }}>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: "40px",
                height: "40px",
                borderRadius: "50%",
                backgroundColor: "#fee2e2",
                color: "#ef4444",
                marginBottom: "12px",
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            </div>
            <h4
              style={{
                margin: "0 0 8px 0",
                color: "#1e293b",
                fontSize: "16px",
              }}
            >
              Unsupported Geometry
            </h4>
            <p
              style={{
                margin: 0,
                fontSize: "14px",
                color: "#64748b",
                lineHeight: "1.5",
              }}
            >
              This editor only supports Point coordinates. The selected feature
              is a <strong style={{ color: "#334155" }}>{geomType}</strong>.
            </p>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginTop: "20px",
              }}
            >
              <button
                onClick={handleCloseModal}
                className="button background_black"
                style={{
                  padding: "8px 24px",
                  cursor: "pointer",
                  color: "white",
                  border: "none",
                }}
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );

  // If we are NOT in the popup editor (i.e. we clicked from the Table),
  // bypass the preview UI and render the modal directly.
  if (!inPopup) {
    return modalContent;
  }

  const mode_map = properties_state.mode_map; //editor || viewer
  return (
    <>
      {/* Preview State */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          width: "100%",
          height: "100%",
        }}
      >
        <p
          className="container background_white text_medium"
          style={{
            margin: 0,
            flex: 1,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={String(displayValue)}
        >
          {displayValue}
        </p>

        {mode_map === "editor" && (
          <button
            className="button background_black"
            onClick={(e) => {
              e.stopPropagation();
              setShowPopupModal(true);
            }}
          >
            Edit
          </button>
        )}
      </div>

      {/* Modal State */}
      {showPopupModal && modalContent}
    </>
  );
}
